<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: 'custom' },
  direction: { type: String, default: 'asc' },
});

const emit = defineEmits(['update:modelValue', 'update:direction']);

const { t } = useI18n();
const open = ref(false);
const sortSearch = ref('');
const sortHighlight = ref(0);
const triggerEl = ref(null);
const dropdownPos = ref({ top: 0, right: 0 });

function calcPos(el) {
  if (!el) return { top: 0, right: 0 };
  const rect = el.getBoundingClientRect();
  return { top: rect.bottom + 4, right: window.innerWidth - rect.right };
}

const SORT_OPTIONS = [
  { key: 'custom', label: t('KANBAN.SORT.CUSTOM') },
  { key: 'stalled', label: t('KANBAN.SORT.STALLED') },
  { key: 'closing_date', label: t('KANBAN.SORT.CLOSING_DATE') },
  { key: 'value', label: t('KANBAN.SORT.VALUE') },
  { key: 'created_at', label: t('KANBAN.SORT.CREATED') },
  { key: 'title', label: t('KANBAN.SORT.TITLE') },
];

const currentLabel = computed(
  () =>
    SORT_OPTIONS.find(o => o.key === props.modelValue)?.label ??
    t('KANBAN.SORT.CUSTOM')
);

const filteredOptions = computed(() => {
  const q = sortSearch.value.toLowerCase();
  if (!q) return SORT_OPTIONS;
  return SORT_OPTIONS.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const navigable = computed(() =>
  filteredOptions.value.map(o => ({ id: o.key }))
);

watch(navigable, () => {
  sortHighlight.value = 0;
});
watch(sortSearch, () => {
  sortHighlight.value = 0;
});

function selectSort(key) {
  emit('update:modelValue', key);
  if (key === 'custom') emit('update:direction', 'asc');
  open.value = false;
  sortSearch.value = '';
}

function toggleDirection() {
  emit('update:direction', props.direction === 'asc' ? 'desc' : 'asc');
}

function keydownHandler(e) {
  if (!open.value) return;
  if (e.key === 'Escape') {
    open.value = false;
    sortSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    sortHighlight.value = Math.min(
      sortHighlight.value + 1,
      navigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    sortHighlight.value = Math.max(sortHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = navigable.value[sortHighlight.value];
    if (item) selectSort(item.id);
  } else if (e.key === 'Backspace') {
    sortSearch.value = sortSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    sortSearch.value += e.key;
  }
}

watch(open, (val, old) => {
  if (val) window.addEventListener('keydown', keydownHandler);
  if (old && !val) {
    window.removeEventListener('keydown', keydownHandler);
    sortSearch.value = '';
  }
});

function isHighlighted(key) {
  return navigable.value.findIndex(i => i.id === key) === sortHighlight.value;
}

function onClickOutside(e) {
  if (
    !e.target.closest('[data-sort-dropdown]') &&
    !e.target.closest('[data-kanban-sort-dropdown]')
  ) {
    open.value = false;
  }
}

onUnmounted(() => {
  window.removeEventListener('keydown', keydownHandler);
  document.removeEventListener('mousedown', onClickOutside);
});

onMounted(() => document.addEventListener('mousedown', onClickOutside));
</script>

<template>
  <div data-sort-dropdown class="flex items-center gap-1 flex-shrink-0">
    <div class="relative">
      <button
        ref="triggerEl"
        class="flex items-center gap-1 text-sm border rounded-lg px-2.5 py-1.5 transition-colors focus:outline-none"
        :class="
          modelValue !== 'custom'
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-11 bg-n-background hover:border-n-container'
        "
        @click.stop="
          open = !open;
          if (open) dropdownPos = calcPos(triggerEl);
          else sortSearch = '';
        "
      >
        <span class="i-lucide-arrow-up-down size-3.5" />
        <span v-if="open && sortSearch" class="text-n-slate-10">{{
          sortSearch
        }}</span>
        <span v-else>{{ currentLabel }}</span>
        <span class="i-lucide-chevron-down size-3.5 text-n-slate-10" />
      </button>
      <Teleport to="body">
        <div
          v-if="open"
          data-kanban-sort-dropdown
          class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-48 overflow-hidden py-1"
          :style="{
            top: dropdownPos.top + 'px',
            right: dropdownPos.right + 'px',
          }"
          @click.stop
        >
          <button
            v-for="opt in filteredOptions"
            :key="opt.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              isHighlighted(opt.key)
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : modelValue === opt.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectSort(opt.key)"
          >
            <span
              v-if="modelValue === opt.key"
              class="i-lucide-check size-3.5 flex-shrink-0 text-n-brand"
            />
            <span v-else class="size-3.5 flex-shrink-0" />
            {{ opt.label }}
          </button>
          <p
            v-if="sortSearch && !filteredOptions.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.FILTERS.NO_RESULTS') }}
          </p>
        </div>
      </Teleport>
    </div>
    <button
      v-if="modelValue !== 'custom'"
      class="flex items-center justify-center size-[30px] border rounded-lg transition-colors border-n-weak text-n-slate-11 bg-n-background hover:border-n-container hover:bg-n-alpha-1 focus:outline-none"
      :title="
        direction === 'asc' ? t('KANBAN.SORT.ASC') : t('KANBAN.SORT.DESC')
      "
      @click="toggleDirection"
    >
      <span
        class="size-3.5 transition-transform"
        :class="
          direction === 'asc'
            ? 'i-lucide-arrow-up-narrow-wide'
            : 'i-lucide-arrow-down-wide-narrow'
        "
      />
    </button>
  </div>
</template>
